import type { InterviewSummary, KpiEvaluation, KpiGrade } from "@scoach/types";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";

import { getDb } from "../../../../lib/firestore.ts";

const GRADE_COLOR: Record<KpiGrade, string> = {
  "No rating": "var(--text-4)",
  Poor: "#ea4335",
  Borderline: "#f9ab00",
  Solid: "#1a73e8",
  Outstanding: "#34a853",
};

/**
 * Live KPI scorecard — subscribes to meetings/{id}/interviewSummary/latest, which
 * the rolling evaluation updates every few minutes, so the interviewer sees
 * competency coverage fill in during the interview.
 */
export function ScorecardPanel({ meetingId }: { meetingId: string }) {
  const [kpis, setKpis] = useState<KpiEvaluation[] | null>(null);

  useEffect(() => {
    try {
      const ref = doc(getDb(), "meetings", meetingId, "interviewSummary", "latest");
      const unsub = onSnapshot(
        ref,
        (snap) => {
          const data = snap.data() as InterviewSummary | undefined;
          setKpis(data?.kpis ?? null);
        },
        () => setKpis(null),
      );
      return () => unsub();
    } catch {
      /* firestore unavailable */
    }
  }, [meetingId]);

  // Group KPIs by pillar for display.
  const byPillar = new Map<string, KpiEvaluation[]>();
  for (const k of kpis ?? []) {
    const arr = byPillar.get(k.pillarName) ?? [];
    arr.push(k);
    byPillar.set(k.pillarName, arr);
  }

  return (
    <section className="panel" style={{ minHeight: 0 }}>
      <div className="panel-head">
        <div style={{ fontWeight: 600, fontSize: 13 }}>Live scorecard</div>
      </div>
      <div style={{ padding: "8px 12px", overflowY: "auto" }}>
        {!kpis || kpis.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--text-3)", margin: "4px 0" }}>
            Grades will appear here as the interview progresses.
          </p>
        ) : (
          [...byPillar.entries()].map(([pillar, list]) => (
            <div key={pillar} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>{pillar}</div>
              {list.map((k) => {
                const grade = k.editedGrade ?? k.grade;
                return (
                  <div
                    key={k.kpiId}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      padding: "2px 0",
                    }}
                  >
                    <span style={{ fontSize: 12 }}>{k.kpiName}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: GRADE_COLOR[grade] }}>
                      {grade}
                    </span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
